import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Archive, RotateCcw, AlertCircle, ChevronRight, Loader2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { getArchivedClients, updateClient } from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

function safeDate(d) {
  if (!d) return '—'
  try { return format(parseISO(d), 'dd MMM yyyy') } catch { return d }
}

function SkeletonRow() {
  return (
    <tr className="border-b border-slate-100">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <td key={i} className="px-4 py-4">
          <div className="skeleton h-4 rounded w-full" />
        </td>
      ))}
    </tr>
  )
}

export default function AdminArchivedClients() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const [unarchiving, setUnarchiving] = useState(null)

  const { data: clients = [], isLoading, error } = useQuery({
    queryKey: ['admin', 'clients', 'archived'],
    queryFn: getArchivedClients,
  })

  const unarchiveMutation = useMutation({
    mutationFn: ({ id }) => updateClient(id, { archived: false }),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      addToast({ type: 'success', message: `"${name}" restored to active clients.` })
      setUnarchiving(null)
    },
    onError: (err) => {
      addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to unarchive client.' })
      setUnarchiving(null)
    },
  })

  function handleUnarchive(client) {
    if (!window.confirm(`Restore "${client.name}" to active clients?`)) return
    setUnarchiving(client.id)
    unarchiveMutation.mutate({ id: client.id, name: client.name })
  }

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Archive size={20} className="text-slate-500" />
              <h2 className="text-xl font-semibold text-slate-800">Archived Clients</h2>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {isLoading ? 'Loading…' : `${clients.length} archived client${clients.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        {error ? (
          <div className="card p-8 text-center">
            <AlertCircle size={32} className="mx-auto text-red-300 mb-2" />
            <p className="text-red-500 text-sm">Failed to load archived clients</p>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Terminated</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Outstanding (all-time)</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="w-24 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {isLoading
                    ? Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)
                    : clients.length === 0
                    ? (
                      <tr>
                        <td colSpan={6} className="py-16 text-center">
                          <Archive size={32} className="mx-auto text-slate-300 mb-2" />
                          <p className="text-slate-400 text-sm">No archived clients</p>
                          <p className="text-slate-300 text-xs mt-1">Archive a client from Edit Client to move them here</p>
                        </td>
                      </tr>
                    )
                    : clients.map((client) => (
                      <tr
                        key={client.id}
                        onClick={() => navigate(`/admin/clients/${client.id}`)}
                        className="hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-4">
                          <p className="font-medium text-slate-700">{client.name}</p>
                          {client.nickname && (
                            <p className="text-xs text-slate-400 mt-0.5">{client.nickname}</p>
                          )}
                        </td>
                        <td className="px-4 py-4 text-slate-500 text-sm">{client.email || '—'}</td>
                        <td className="px-4 py-4">
                          {client.termination_date ? (
                            <span className="text-slate-600 text-sm">{safeDate(client.termination_date)}</span>
                          ) : (
                            <span className="text-slate-300 text-xs italic">Not set</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className={client.outstanding > 0 ? 'text-red-600 font-semibold' : 'text-slate-700'}>
                            {formatCurrency(client.outstanding)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {client.overdue ? (
                            <span className="badge bg-red-100 text-red-700 text-xs">
                              <AlertCircle size={10} className="mr-1" /> Overdue
                            </span>
                          ) : client.outstanding > 0 ? (
                            <span className="badge bg-amber-100 text-amber-700 text-xs">Unpaid</span>
                          ) : (
                            <span className="badge bg-slate-100 text-slate-500 text-xs">Settled</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleUnarchive(client) }}
                              disabled={unarchiving === client.id}
                              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 transition-colors disabled:opacity-50"
                              title="Restore to active clients"
                            >
                              {unarchiving === client.id
                                ? <Loader2 size={11} className="animate-spin" />
                                : <RotateCcw size={11} />}
                              Restore
                            </button>
                            <ChevronRight size={14} className="text-slate-300" />
                          </div>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </>
  )
}
